import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Sparkles, ArrowUpRight, ArrowLeft } from "lucide-react";
import { useLang } from "../LanguageContext";
import EditableText from "../components/EditableText";
import { useCurrency } from "../CurrencyContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * The AI Consultation Concierge — one question at a time. 4 questions, then
 * Claude returns a curated ritual (2-3 services). Bilingual EN/FR.
 */
const QUESTIONS_EN = [
  {
    key: "concern",
    title: "What feels most on your mind right now?",
    sub: "Pick the one closest to your wish.",
    choices: [
      { v: "fine-lines", label: "Fine lines or expression marks" },
      { v: "lips-volume", label: "Lip definition or volume" },
      { v: "tired-skin", label: "Tired, dull, low-energy skin" },
      { v: "lashes-brows", label: "Lashes & brows — frame the face" },
      { v: "general-glow", label: "Just an all-over glow" },
    ],
  },
  {
    key: "occasion",
    title: "Is this for an occasion, or a way of life?",
    sub: "We tailor cadence and intensity accordingly.",
    choices: [
      { v: "event", label: "An upcoming event — wedding, shoot, travel" },
      { v: "routine", label: "A new monthly ritual" },
      { v: "reset", label: "A reset after a busy season" },
      { v: "first-time", label: "First time — gentle introduction" },
    ],
  },
  {
    key: "comfort",
    title: "How invasive are you comfortable with?",
    sub: "Honest answers give honest recommendations.",
    choices: [
      { v: "non-invasive", label: "Non-invasive only — no needles" },
      { v: "minimal", label: "Open to injectables in small doses" },
      { v: "full", label: "Open to the full menu — I trust the expert" },
    ],
  },
  {
    key: "budget",
    title: "What feels right for this visit?",
    sub: "An indication only — we'll never push.",
    choices: [
      { v: "discovery", label: "Under 50,000 FCFA — discovery" },
      { v: "mid", label: "50,000 – 150,000 FCFA — considered" },
      { v: "signature", label: "Above 150,000 FCFA — signature" },
      { v: "open", label: "I prefer not to set a number" },
    ],
  },
];

const QUESTIONS_FR = [
  {
    key: "concern",
    title: "Qu'est-ce qui vous préoccupe le plus en ce moment ?",
    sub: "Choisissez ce qui vous parle.",
    choices: [
      { v: "fine-lines", label: "Ridules ou marques d'expression" },
      { v: "lips-volume", label: "Définition ou volume des lèvres" },
      { v: "tired-skin", label: "Peau fatiguée, terne, manque d'éclat" },
      { v: "lashes-brows", label: "Cils & sourcils — encadrer le visage" },
      { v: "general-glow", label: "Simplement un éclat général" },
    ],
  },
  {
    key: "occasion",
    title: "S'agit-il d'une occasion, ou d'un art de vivre ?",
    sub: "Nous adaptons cadence et intensité.",
    choices: [
      { v: "event", label: "Un événement à venir — mariage, shooting, voyage" },
      { v: "routine", label: "Un nouveau rituel mensuel" },
      { v: "reset", label: "Une remise à neuf après une saison chargée" },
      { v: "first-time", label: "Première fois — introduction douce" },
    ],
  },
  {
    key: "comfort",
    title: "Quel niveau d'intervention vous convient ?",
    sub: "L'honnêteté garantit la justesse de la recommandation.",
    choices: [
      { v: "non-invasive", label: "Non-invasif uniquement — pas d'aiguilles" },
      { v: "minimal", label: "Ouverte aux injectables à petites doses" },
      { v: "full", label: "Ouverte à toute la carte — je fais confiance à l'experte" },
    ],
  },
  {
    key: "budget",
    title: "Quel budget vous semble juste pour cette visite ?",
    sub: "À titre indicatif uniquement.",
    choices: [
      { v: "discovery", label: "Moins de 50 000 FCFA — découverte" },
      { v: "mid", label: "50 000 – 150 000 FCFA — réfléchie" },
      { v: "signature", label: "Plus de 150 000 FCFA — signature" },
      { v: "open", label: "Je préfère ne pas fixer de chiffre" },
    ],
  },
];

export default function Consultation() {
  const { lang } = useLang();
  const { format } = useCurrency();
  const nav = useNavigate();
  const QUESTIONS = lang === "fr" ? QUESTIONS_FR : QUESTIONS_EN;

  const [step, setStep] = useState(-1); // -1 = intro, 0..n-1 = question, n = loading/result
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const start = () => setStep(0);
  const back = () => setStep((s) => Math.max(-1, s - 1));

  const choose = async (q, choice) => {
    const next = { ...answers, [q.key]: choice.label };
    setAnswers(next);
    if (step + 1 < QUESTIONS.length) {
      setStep(step + 1);
      return;
    }
    // Submit
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/consultation`, { answers: next, language: lang });
      setResult(res.data);
    } catch (e) {
      setError(lang === "fr"
        ? "La Concierge est momentanément indisponible. Veuillez réessayer dans un instant."
        : "The Concierge is briefly unavailable. Please try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  const restart = () => {
    setStep(-1);
    setAnswers({});
    setResult(null);
    setError("");
  };

  // ----- RESULT -----
  if (result) {
    return (
      <main data-testid="consultation-page" className="pt-32 min-h-screen px-6 md:px-12 lg:px-20 py-16 md:py-24">
        <div className="max-w-3xl mx-auto">
          <Sparkles size={28} strokeWidth={0.8} className="text-primary mb-8" />
          <p className="qw-overline mb-4">{lang === "fr" ? "Votre rituel personnalisé" : "Your bespoke ritual"}</p>
          <h1 data-testid="consultation-ritual-name" className="font-serif text-4xl md:text-5xl lg:text-6xl text-foreground font-light leading-tight mb-8 text-balance">
            {result.ritual_name}
          </h1>
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-14 max-w-2xl">
            {result.intro}
          </p>

          <div className="qw-divider mb-14" />

          <p className="qw-overline mb-8">{lang === "fr" ? "Notre composition" : "The composition"}</p>
          <ul className="space-y-10 mb-16">
            {result.recommendations.map((rec, idx) => (
              <li key={rec.service_id} data-testid={`consult-rec-${rec.service_id}`} className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-baseline border-b border-border pb-10">
                <div className="md:col-span-1 font-serif text-3xl text-primary qw-num">0{idx + 1}</div>
                <div className="md:col-span-8">
                  <h3 className="font-serif text-2xl md:text-3xl text-foreground mb-3">{rec.name}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-4">{rec.reason}</p>
                  {rec.duration && rec.duration !== "—" && (
                    <p className="text-[0.65rem] tracking-wider uppercase text-muted-foreground">
                      {lang === "fr" ? "Durée" : "Duration"} · {rec.duration}
                    </p>
                  )}
                </div>
                <div className="md:col-span-3 md:text-right">
                  <p className="font-serif text-xl md:text-2xl text-primary qw-num mb-3">
                    {rec.price_fcfa === 0 ? (lang === "fr" ? "Sur consultation" : "On consultation") : format(rec.price_fcfa)}
                  </p>
                  <button
                    onClick={() => nav(`/booking?service=${rec.service_id}`)}
                    data-testid={`consult-book-${rec.service_id}`}
                    className="qw-link-underline text-[0.65rem] tracking-[0.25em] uppercase text-foreground/85 hover:text-primary"
                  >
                    {lang === "fr" ? "Réserver" : "Book"} <ArrowUpRight size={11} strokeWidth={1.2} className="inline" />
                  </button>
                </div>
              </li>
            ))}
          </ul>

          <div className="flex flex-wrap items-center gap-5">
            <button
              onClick={() => nav(`/booking?service=${result.recommendations[0]?.service_id || ""}`)}
              data-testid="consult-book-primary"
              className="qw-btn qw-btn-primary"
            >
              {lang === "fr" ? "Réserver ce rituel" : "Book this ritual"}
              <ArrowUpRight size={14} strokeWidth={1.2} />
            </button>
            <button
              onClick={restart}
              data-testid="consult-restart-btn"
              className="qw-btn qw-btn-outline"
            >
              {lang === "fr" ? "Recommencer" : "Start over"}
            </button>
          </div>
        </div>
      </main>
    );
  }

  // ----- LOADING -----
  if (loading) {
    return (
      <main data-testid="consultation-page" className="pt-32 min-h-screen flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <Sparkles size={32} strokeWidth={0.6} className="text-primary mx-auto mb-8 animate-pulse" />
          <p className="qw-overline mb-3">{lang === "fr" ? "Composition en cours" : "Composing your ritual"}</p>
          <h2 className="font-serif text-2xl md:text-3xl text-foreground font-light leading-snug">
            {lang === "fr"
              ? "La Concierge analyse vos réponses…"
              : "The Concierge is reading between the lines…"}
          </h2>
        </div>
      </main>
    );
  }

  // ----- INTRO -----
  if (step === -1) {
    return (
      <main data-testid="consultation-page" className="pt-32 min-h-screen px-6 md:px-12 lg:px-20 py-16 md:py-24">
        <div className="max-w-2xl">
          <Sparkles size={28} strokeWidth={0.8} className="text-primary mb-8" />
          <p className="qw-overline mb-5">
            <EditableText kEn="txt_consult_overline_en" kFr="txt_consult_overline_fr" label="Consultation overline"
              fallback={{ en: "The AI Concierge", fr: "La Concierge IA" }} />
          </p>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-foreground font-light leading-tight tracking-tight mb-8 text-balance">
            <EditableText kEn="txt_consult_title_en" kFr="txt_consult_title_fr" label="Consultation title"
              fallback={{
                en: "A ritual composed for you, in 60 seconds.",
                fr: "Un rituel composé pour vous, en 60 secondes.",
              }} />
          </h1>
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-12 max-w-xl">
            <EditableText kEn="txt_consult_body_en" kFr="txt_consult_body_fr" label="Consultation intro"
              fallback={{
                en: "Four discreet questions. Our AI concierge, trained on our catalog, will propose two or three services in perfect harmony.",
                fr: "Quatre questions discrètes. Notre concierge IA, formée à notre catalogue, vous proposera deux ou trois prestations en parfaite harmonie.",
              }} />
          </p>
          <button
            onClick={start}
            data-testid="consult-start-btn"
            className="qw-btn qw-btn-primary"
          >
            <EditableText kEn="txt_consult_begin_en" kFr="txt_consult_begin_fr" label="Consultation button"
              fallback={{ en: "Begin the consultation", fr: "Commencer" }} />
            <ArrowUpRight size={14} strokeWidth={1.2} />
          </button>
        </div>
      </main>
    );
  }

  // ----- QUESTION -----
  const q = QUESTIONS[step];
  return (
    <main data-testid="consultation-page" className="pt-32 min-h-screen px-6 md:px-12 lg:px-20 py-12 md:py-20">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-12">
          <p className="qw-overline">
            0{step + 1} <span className="text-foreground/40 mx-2">/</span> 0{QUESTIONS.length}
          </p>
          {step > 0 && (
            <button
              onClick={back}
              data-testid="consult-back-btn"
              className="text-[0.65rem] tracking-[0.25em] uppercase text-foreground/70 hover:text-primary flex items-center gap-2"
            >
              <ArrowLeft size={11} strokeWidth={1.2} />
              {lang === "fr" ? "Précédent" : "Back"}
            </button>
          )}
        </div>

        <h1 data-testid={`consult-q-${q.key}`} className="font-serif text-3xl md:text-5xl text-foreground font-light leading-tight tracking-tight mb-4 text-balance qw-fade-in">
          {q.title}
        </h1>
        <p className="text-sm md:text-base text-muted-foreground mb-12">{q.sub}</p>

        <div className="space-y-4 qw-fade-in">
          {q.choices.map((c) => (
            <button
              key={c.v}
              data-testid={`consult-${q.key}-${c.v}`}
              onClick={() => choose(q, c)}
              className="group block w-full text-left px-6 py-5 border border-border hover:border-primary hover:bg-primary/5 transition-all duration-500"
            >
              <span className="font-serif text-lg md:text-xl text-foreground group-hover:text-primary transition-colors">
                {c.label}
              </span>
            </button>
          ))}
        </div>

        {error && (
          <div className="mt-10 border border-destructive/50 p-5">
            <p className="text-sm text-destructive">{error}</p>
            <button onClick={() => choose(QUESTIONS[step], { label: answers[QUESTIONS[step].key] || "" })} className="mt-4 qw-link-underline text-xs tracking-wider uppercase text-primary">
              {lang === "fr" ? "Réessayer" : "Try again"}
            </button>
          </div>
        )}
      </div>
    </main>
  );
}
